import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  updateProfile,
  User,
  UserCredential,
  onAuthStateChanged,
  Unsubscribe,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../config/firebase';
import { EncryptionService } from './EncryptionService';

export interface AdminUser {
  uid: string;
  email: string;
  displayName?: string;
  fullNames: string;
  lastName: string;
  cellPhone: string;
  isAdmin: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export type UserType = 'member' | 'visitor';

export interface UserProfile {
  uid: string;
  email: string;
  userType: UserType; // 'member' or 'visitor'
  displayName?: string;
  profilePictureURL?: string; // Profile picture URL from Firebase Storage
  title: string;
  initials: string;
  fullNames: string;
  preferredName: string;
  lastName: string;
  dateOfBirth: string;
  age: string;
  rsaIdNumber: string; // Encrypted when stored in database
  sahgcaNumber?: string; // Optional SAHGCA Number
  gender: string;
  occupation: string;
  preferredLanguage: string;
  otherLanguage: string;
  ethnicity: string;
  phoneNumber: string;
  cellPhone: string;
  address: {
    street: string;
    city: string;
    province: string;
    postalCode: string;
  };
  emergencyContact: {
    name: string;
    relationship: string;
    phoneNumber: string;
  };
  membership: {
    membershipType: string;
    membershipNumber: string;
    membershipStartDate: string; // YYYY-MM-DD format
    membershipDueDate: string; // YYYY-MM-DD format
    membershipStatus: 'Pending' | 'Paid' | 'Renew' | 'Arrears' | 'Not Paid' | 'Inactive';
    otherAssociation: string;
    otherAssociationName: string;
    paymentMethod: string;
    amount: number;
    proofOfPaymentURL: string | null; // Store Firebase Storage download URL
    sajwv?: {
      number?: string;
      claimedAt?: Date;
      status?: 'claimed' | 'verified' | 'rejected';
      verifiedBy?: string;
      verifiedAt?: Date;
      rejectedReason?: string;
    } | null;
    balanceDue?: number;
    renewalYear?: number;
    lastPaidAt?: Date;
    renewalProofSubmittedAt?: Date | null;
    renewalProofPending?: boolean;
    yocoCheckoutId?: string;
    yocoCheckoutAmountCents?: number;
    yocoCheckoutPurpose?: string;
    yocoCheckoutCoverageYear?: number;
    unmatchedPaymentCents?: number;
    unmatchedPaymentId?: string;
    unmatchedPaymentAt?: Date;
  };
  termsAccepted: boolean;
  // Set when an admin issues a temporary password; cleared after the member chooses a new one
  mustChangePassword?: boolean;
  isAdmin: boolean; // Deprecated - use role instead
  role: 'member' | 'regional_admin' | 'members_admin' | 'event_manager' | 'super_admin' | 'event_staff' | 'club_admin';
  assignedRegion?: string; // For regional_admin - the region they can manage
  clubId?: string; // Club this event organiser or club admin orders for
  assignedEvents?: string[]; // For event_staff - array of event IDs they can access
  isInternationalRangeOfficer?: boolean; // Optional; absent or false means a national RO
  // Online Range Officer course training record. Written only by the
  // submitRangeOfficerCourse Cloud Function; clients cannot create or change it.
  rangeOfficerCourse?: {
    passed: boolean;
    bestScore: number;
    passedAt?: Date;
    lastAttemptAt?: Date;
  };
  createdAt: Date;
  updatedAt: Date;
}

// Simplified profile for visitors
export interface VisitorProfile {
  uid: string;
  email: string;
  userType: 'visitor';
  firstName: string;
  lastName: string;
  phone: string;
  rsaIdNumber: string; // Encrypted
  isBuddyTestUser?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export type UserRole = 'member' | 'regional_admin' | 'members_admin' | 'event_manager' | 'super_admin' | 'event_staff' | 'club_admin';

export const USER_ROLES = {
  MEMBER: 'member' as UserRole,
  REGIONAL_ADMIN: 'regional_admin' as UserRole,
  MEMBERS_ADMIN: 'members_admin' as UserRole,
  EVENT_MANAGER: 'event_manager' as UserRole,
  SUPER_ADMIN: 'super_admin' as UserRole,
  EVENT_STAFF: 'event_staff' as UserRole,
  CLUB_ADMIN: 'club_admin' as UserRole,
};

export const ROLE_LABELS = {
  member: 'Member',
  regional_admin: 'Provincial Admin',
  members_admin: 'Members Admin',
  event_manager: 'Event Coordinator',
  super_admin: 'Super Admin',
  event_staff: 'Event Staff',
  club_admin: 'Event Admin',
};

export const ROLE_DESCRIPTIONS = {
  member: 'Standard member with no admin privileges',
  regional_admin: 'Can manage calendar, events, clubs, and target orders in their province, and view members and visitors in that province',
  members_admin: 'Can manage members but cannot grant admin rights',
  event_manager: 'Can run calendar, events, stats office, and scores for the province of their linked club, and place target orders for that club',
  super_admin: 'Full access to all admin features',
  event_staff: 'Stats office or event staff with access to assigned events only',
  club_admin: 'Can run calendar, events, stats office, and scores for the province of their linked club, and place target orders for that club',
};

export class AuthService {
  // Register new user
  static async registerUser(
    email: string, 
    password: string, 
    profile: Omit<UserProfile, 'uid' | 'email' | 'createdAt' | 'updatedAt'>
  ): Promise<UserCredential> {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update display name
      if (userCredential.user) {
        await updateProfile(userCredential.user, {
          displayName: `${profile.fullNames}`
        });
      }

      // Save user profile to Firestore
      const userProfile: UserProfile = {
        ...profile,
        uid: userCredential.user.uid,
        email,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      console.log('Saving user profile to Firestore:', userProfile);
      await setDoc(doc(db, 'users', userCredential.user.uid), userProfile);
      console.log('User profile saved successfully');

      return userCredential;
    } catch (error) {
      console.error('Error registering user:', error);
      throw error;
    }
  }

  // Register visitor (simplified registration for event attendees)
  static async registerVisitor(
    email: string,
    password: string,
    visitorInfo: {
      firstName: string;
      lastName: string;
      phone: string;
      idNumber: string;
    }
  ): Promise<UserCredential> {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update display name
      if (userCredential.user) {
        await updateProfile(userCredential.user, {
          displayName: `${visitorInfo.firstName} ${visitorInfo.lastName}`
        });
      }

      // Encrypt ID number before storing
      const encryptedIdNumber = EncryptionService.encryptIdNumber(visitorInfo.idNumber);

      // Save visitor profile to Firestore
      const visitorProfile: VisitorProfile = {
        uid: userCredential.user.uid,
        email,
        userType: 'visitor',
        firstName: visitorInfo.firstName,
        lastName: visitorInfo.lastName,
        phone: visitorInfo.phone,
        rsaIdNumber: encryptedIdNumber,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      console.log('Saving visitor profile to Firestore');
      await setDoc(doc(db, 'users', userCredential.user.uid), visitorProfile);
      console.log('Visitor profile saved successfully');

      return userCredential;
    } catch (error) {
      console.error('Error registering visitor:', error);
      throw error;
    }
  }

  /**
   * Get display name from any user profile (member or visitor)
   * Handles both profile structures safely
   */
  static getDisplayName(profile: UserProfile | VisitorProfile | null | undefined): string {
    if (!profile) return 'Unknown';
    
    // Check for visitor profile (has firstName)
    if ('firstName' in profile && profile.firstName) {
      return `${profile.firstName} ${profile.lastName || ''}`.trim();
    }
    
    // Check for member profile (has preferredName or fullNames)
    if ('preferredName' in profile || 'fullNames' in profile) {
      const memberProfile = profile as UserProfile;
      const firstName = memberProfile.preferredName || memberProfile.fullNames || '';
      return `${firstName} ${memberProfile.lastName || ''}`.trim();
    }
    
    return profile.email || 'Unknown';
  }

  // Sign in user
  static async signInUser(email: string, password: string): Promise<UserCredential> {
    try {
      return await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Error signing in user:', error);
      throw error;
    }
  }

  // Sign out user
  static async signOutUser(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out user:', error);
      throw error;
    }
  }

  // Get user profile
  static async getUserProfile(uid: string): Promise<UserProfile | null> {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const profile = userDoc.data() as UserProfile;
        
        // Decrypt the ID number if it exists
        // Using safeDecrypt which handles legacy unencrypted data
        if (profile.rsaIdNumber && profile.rsaIdNumber.trim() !== '') {
          profile.rsaIdNumber = EncryptionService.decryptIdNumber(profile.rsaIdNumber);
        }
        
        return profile;
      }
      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      throw error;
    }
  }

  // Update user profile
  static async updateUserProfile(uid: string, updates: Partial<UserProfile>): Promise<void> {
    try {
      const userRef = doc(db, 'users', uid);
      
      // Encrypt the ID number if it's being updated
      const encryptedUpdates = { ...updates };
      if (updates.rsaIdNumber !== undefined) {
        if (updates.rsaIdNumber && updates.rsaIdNumber.trim() !== '') {
          encryptedUpdates.rsaIdNumber = EncryptionService.encryptIdNumber(updates.rsaIdNumber);
        } else {
          encryptedUpdates.rsaIdNumber = '';
        }
      }
      
      if (encryptedUpdates.membership && typeof encryptedUpdates.membership === 'object') {
        const currentSnap = await getDoc(userRef);
        const existingMembership = currentSnap.data()?.membership;
        if (existingMembership && typeof existingMembership === 'object') {
          const incoming = { ...encryptedUpdates.membership } as Record<string, unknown>;
          const locked = [
            'membershipStatus',
            'membershipDueDate',
            'renewalYear',
            'lastPaidAt',
            'balanceDue',
            'yocoCheckoutId',
            'yocoCheckoutAmountCents',
            'yocoCheckoutPurpose',
            'yocoCheckoutCoverageYear',
            'unmatchedPaymentCents',
            'unmatchedPaymentId',
            'unmatchedPaymentAt',
          ] as const;
          locked.forEach((key) => {
            if (existingMembership[key] !== undefined) {
              incoming[key] = existingMembership[key];
            } else {
              delete incoming[key];
            }
          });
          const existingSajwv = existingMembership.sajwv;
          if (existingSajwv && typeof existingSajwv === 'object') {
            const incomingSajwv = incoming.sajwv && typeof incoming.sajwv === 'object'
              ? { ...(incoming.sajwv as Record<string, unknown>) }
              : { ...existingSajwv };
            const oldStatus = existingSajwv.status;
            const newStatus = incomingSajwv.status;
            const statusAllowed = newStatus === oldStatus
              || (newStatus === 'claimed' && (oldStatus == null || oldStatus === 'claimed'));
            if (!statusAllowed && oldStatus !== undefined) incomingSajwv.status = oldStatus;
            (['verifiedBy', 'verifiedAt', 'rejectedReason'] as const).forEach((key) => {
              if (existingSajwv[key] !== undefined) incomingSajwv[key] = existingSajwv[key];
            });
            incoming.sajwv = incomingSajwv;
          }
          encryptedUpdates.membership = incoming as UserProfile['membership'];
        }
      }

      await updateDoc(userRef, {
        ...encryptedUpdates,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  // Update proof of payment URL
  static async updateProofOfPayment(uid: string, proofOfPaymentURL: string | null): Promise<void> {
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        'membership.proofOfPaymentURL': proofOfPaymentURL,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating proof of payment:', error);
      throw new Error('Failed to update proof of payment');
    }
  }

  // Get current user
  static getCurrentUser(): User | null {
    return auth.currentUser;
  }

  // Check if user is authenticated
  static isAuthenticated(): boolean {
    return auth.currentUser !== null;
  }

  // Login user (alias for signInUser)
  static async loginUser(email: string, password: string): Promise<UserCredential> {
    return this.signInUser(email, password);
  }

  // Logout user (alias for signOutUser)
  static async logoutUser(): Promise<void> {
    return this.signOutUser();
  }

  // Get current user profile
  static async getCurrentUserProfile(): Promise<UserProfile> {
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      throw new Error('No authenticated user');
    }
    
    const profile = await this.getUserProfile(currentUser.uid);
    if (!profile) {
      throw new Error('User profile not found');
    }
    
    return profile;
  }

  // Register admin user
  static async registerAdminUser(
    email: string, 
    password: string, 
    profile: Omit<AdminUser, 'uid' | 'email' | 'isAdmin' | 'createdAt' | 'updatedAt'>
  ): Promise<UserCredential> {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update display name
      if (userCredential.user) {
        await updateProfile(userCredential.user, {
          displayName: `${profile.fullNames}`
        });
      }

      // Save admin user profile to Firestore
      const adminProfile: AdminUser = {
        ...profile,
        uid: userCredential.user.uid,
        email,
        isAdmin: true,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      console.log('Saving admin profile to Firestore:', adminProfile);
      await setDoc(doc(db, 'users', userCredential.user.uid), adminProfile);
      console.log('Admin profile saved successfully');

      return userCredential;
    } catch (error) {
      console.error('Error registering admin user:', error);
      throw error;
    }
  }

  // Check if user is admin
  static async isUserAdmin(uid: string): Promise<boolean> {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.isAdmin === true;
      }
      return false;
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  }

  // Get current user admin status
  static async isCurrentUserAdmin(): Promise<boolean> {
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      return false;
    }
    return this.isUserAdmin(currentUser.uid);
  }

  // Auth state change listener
  static onAuthStateChanged(callback: (user: User | null) => void): Unsubscribe {
    return onAuthStateChanged(auth, callback);
  }

  // Password Reset Methods
  private static functions = getFunctions();

  /**
   * Request a password reset email via SendGrid
   * This sends a custom email with a reset link
   */
  static async requestPasswordReset(email: string): Promise<{ success: boolean; message: string }> {
    try {
      const requestPasswordResetFn = httpsCallable(this.functions, 'requestPasswordReset');
      const result = await requestPasswordResetFn({ email: email.toLowerCase() });
      return result.data as { success: boolean; message: string };
    } catch (error: any) {
      console.error('Error requesting password reset:', error);
      throw new Error(error.message || 'Failed to send password reset email. Please try again.');
    }
  }

  /**
   * Verify a password reset token
   */
  static async verifyPasswordResetToken(token: string): Promise<{ valid: boolean; email?: string; error?: string }> {
    try {
      const verifyTokenFn = httpsCallable(this.functions, 'verifyPasswordResetToken');
      const result = await verifyTokenFn({ token });
      return result.data as { valid: boolean; email?: string; error?: string };
    } catch (error: any) {
      console.error('Error verifying reset token:', error);
      return { valid: false, error: 'Failed to verify token. Please try again.' };
    }
  }

  /**
   * Reset password using a token (from email link)
   */
  static async resetPasswordWithToken(token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      const resetPasswordFn = httpsCallable(this.functions, 'resetPasswordWithToken');
      const result = await resetPasswordFn({ token, newPassword });
      return result.data as { success: boolean; message: string };
    } catch (error: any) {
      console.error('Error resetting password:', error);
      throw new Error(error.message || 'Failed to reset password. Please try again.');
    }
  }

  /**
   * Request a password change email for logged-in users
   */
  static async requestPasswordChangeEmail(): Promise<{ success: boolean; message: string }> {
    const currentUser = this.getCurrentUser();
    if (!currentUser || !currentUser.email) {
      throw new Error('No authenticated user');
    }

    try {
      const sendPasswordChangeEmailFn = httpsCallable(this.functions, 'sendPasswordChangeEmail');
      const result = await sendPasswordChangeEmailFn({ email: currentUser.email });
      return result.data as { success: boolean; message: string };
    } catch (error: any) {
      console.error('Error requesting password change email:', error);
      throw new Error(error.message || 'Failed to send password change email. Please try again.');
    }
  }

  /**
   * Change password directly (requires current password for reauthentication)
   */
  static async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const currentUser = this.getCurrentUser();
    if (!currentUser || !currentUser.email) {
      throw new Error('No authenticated user');
    }

    try {
      // Re-authenticate the user first
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);

      // Update the password
      await updatePassword(currentUser, newPassword);

      try {
        await this.clearMustChangePassword();
      } catch (flagError) {
        console.error('Error clearing mustChangePassword after password change:', flagError);
      }
    } catch (error: any) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        throw new Error('Current password is incorrect');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('New password is too weak. Please use at least 6 characters.');
      } else if (error.code === 'auth/requires-recent-login') {
        throw new Error('Please log out and log back in before changing your password.');
      }
      throw new Error(error.message || 'Failed to change password');
    }
  }

  // Email Update Methods

  /**
   * Admin: generate a temporary password for a member, email it, and force a change on next login.
   */
  static async adminSetTempPassword(targetUserId: string): Promise<{
    success: boolean;
    tempPassword: string;
    email: string;
    emailSent?: boolean;
  }> {
    try {
      const adminSetTempPasswordFn = httpsCallable(this.functions, 'adminSetTempPassword');
      const result = await adminSetTempPasswordFn({ targetUserId });
      return result.data as {
        success: boolean;
        tempPassword: string;
        email: string;
        emailSent?: boolean;
      };
    } catch (error: any) {
      console.error('Error setting temporary password:', error);
      const message = error.message || 'Failed to set temporary password';
      throw new Error(message);
    }
  }

  /**
   * Clear the forced-change flag on the current user's profile.
   */
  static async clearMustChangePassword(): Promise<void> {
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      throw new Error('No authenticated user');
    }

    await updateDoc(doc(db, 'users', currentUser.uid), {
      mustChangePassword: false,
      updatedAt: new Date(),
    });
  }

  /**
   * Update user's email address (Admin or self)
   * This calls a Cloud Function that updates both Firebase Auth and Firestore
   */
  static async updateUserEmail(targetUserId: string, newEmail: string): Promise<{ success: boolean; message: string }> {
    try {
      const updateEmailFn = httpsCallable(this.functions, 'updateUserEmail');
      const result = await updateEmailFn({ targetUserId, newEmail: newEmail.toLowerCase() });
      return result.data as { success: boolean; message: string };
    } catch (error: any) {
      console.error('Error updating email:', error);
      // Extract the actual error message from Firebase
      const message = error.message || 'Failed to update email address';
      throw new Error(message);
    }
  }

  /**
   * Update current user's email (requires password verification first)
   */
  static async updateOwnEmail(newEmail: string, currentPassword: string): Promise<{ success: boolean; message: string }> {
    const currentUser = this.getCurrentUser();
    if (!currentUser || !currentUser.email) {
      throw new Error('No authenticated user');
    }

    try {
      // Re-authenticate the user first
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);

      // Now call the cloud function to update email
      return await this.updateUserEmail(currentUser.uid, newEmail);
    } catch (error: any) {
      console.error('Error updating own email:', error);
      if (error.code === 'auth/wrong-password') {
        throw new Error('Current password is incorrect');
      } else if (error.code === 'auth/requires-recent-login') {
        throw new Error('Please log out and log back in before changing your email.');
      }
      throw new Error(error.message || 'Failed to update email address');
    }
  }

  /**
   * Verify user's password (useful before sensitive operations)
   */
  static async verifyPassword(password: string): Promise<boolean> {
    const currentUser = this.getCurrentUser();
    if (!currentUser || !currentUser.email) {
      throw new Error('No authenticated user');
    }

    try {
      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential);
      return true;
    } catch (error: any) {
      if (error.code === 'auth/wrong-password') {
        return false;
      }
      throw error;
    }
  }
} 